import React from 'react';
import {Link} from 'react-router-dom';
import Picture from '../../components/Picture';
import {DamageTrack, HumanityTrack, HungerDrops} from './SheetParts';
import {CHARACTER_STATUS, getHealthMax, getWillpowerMax} from '../character';
import {CLANS_BY_ID} from '../data/clans';
import {localized} from '../data/traits';
import {vtmT, currentLanguage} from '../i18n';
import '../../styles/vtm/roll.css';
import '../../styles/vtm/campaignHome.css';

const DmSigil = () => (
  <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M8 34 L12 16 L20 26 L24 12 L28 26 L36 16 L40 34 Z M10 38 H38" /></svg>
);

const lastUpdate = (character) => (character.lastUpdateAt && character.lastUpdateAt.seconds) || 0;
const isDraft = (character) => character.status === CHARACTER_STATUS.DRAFT;

/** « Brujah · 13e génération », ou le statut tant que la fiche n'est pas validée. */
const Identity = ({character}) => {
  const clan = localized(CLANS_BY_ID[character.vtm?.clan]?.name, currentLanguage());
  const parts = [clan, character.vtm?.generation && `${vtmT('generationValue', {value: character.vtm.generation})} ${vtmT('generation').toLowerCase()}`].filter(Boolean);
  return (
    <span className="vtmHomeIdentity">
      {parts.join(' · ')}
      {character.status !== CHARACTER_STATUS.APPROVED && (
        <span className={`vtmHomeStatus ${character.status}`}>{vtmT(`status_${character.status}`)}</span>
      )}
    </span>
  );
};

/** Les quatre jauges de jeu, en lecture seule. */
const Gauges = ({sheet}) => (
  <div className="vtmHomeGauges">
    <div className="vtmHomeGauge">
      <span className="vtmHomeGaugeLabel">{vtmT('health')}</span>
      <DamageTrack max={getHealthMax(sheet)} track={sheet.health} label={vtmT('health')} />
    </div>
    <div className="vtmHomeGauge">
      <span className="vtmHomeGaugeLabel">{vtmT('willpower')}</span>
      <DamageTrack max={getWillpowerMax(sheet)} track={sheet.willpower} label={vtmT('willpower')} />
    </div>
    <div className="vtmHomeGauge">
      <span className="vtmHomeGaugeLabel">{vtmT('hunger')}</span>
      <HungerDrops hunger={sheet.hunger} />
    </div>
    <div className="vtmHomeGauge">
      <span className="vtmHomeGaugeLabel">{vtmT('humanity')}</span>
      <HumanityTrack humanity={sheet.humanity} stains={sheet.stains} />
    </div>
  </div>
);

const CharacterRow = ({character, baseUrl, onSelect, showHunger}) => (
  <Link className="vtmHomeRow" to={`${baseUrl}/${character.uid}`} onClick={() => onSelect(character)}>
    <span className="vtmHomeRowPortrait"><Picture character={character} frame="" /></span>
    <span className="vtmHomeRowText">
      <span className="vtmHomeRowName">{character.name || vtmT('newCharacter')}</span>
      <Identity character={character} />
      {showHunger && !isDraft(character) && character.vtm && <HungerDrops hunger={character.vtm.hunger} />}
    </span>
  </Link>
);

/**
 * Accueil d'une campagne VtM : le joueur voit son vampire en grand, le MJ sa vue puis la coterie.
 * Les données et la création de brouillon restent dans containers/Characters.js.
 */
const VtmCampaignHome = ({campaign, characters, loading, userUid, baseUrl, onSelectCharacter, onCreateCharacter}) => {
  if (loading) {
    return (
      <div className="vtmHome" aria-busy="true">
        <div className="vtmHomeSkeleton header" />
        <div className="vtmHomeSkeleton hero" />
      </div>
    );
  }

  const isDm = userUid === campaign.idUserDm;
  const sorted = [...characters].sort((a, b) => lastUpdate(b) - lastUpdate(a));
  const [mainCharacter, ...otherCharacters] = sorted;
  const createdAt = campaign.createdAt && campaign.createdAt.seconds
    ? new Date(campaign.createdAt.seconds * 1000).toLocaleDateString(currentLanguage(), {month: 'long', year: 'numeric'})
    : null;

  return (
    <div className="vtmHome">
      <div className="vtmHomeHeader">
        <Link className="vtmHomeBack" to="/campaigns">← {vtmT('myCampaigns')}</Link>
        <h1 className="vtmHomeTitle">{campaign.name}</h1>
        <p className="vtmHomeMeta">
          {campaign.createdBy && <span>{vtmT('dm')} · {campaign.createdBy}</span>}
          {createdAt && <span>{createdAt}</span>}
          {isDm && campaign.invitationCode && (
            <span>{vtmT('invitationCode')} · <strong>{campaign.invitationCode}</strong></span>
          )}
        </p>
      </div>

      {isDm ? (
        <>
          <Link className="vtmHomeDm" to={`${baseUrl}/dm`} onClick={() => onSelectCharacter(null)}>
            <span className="vtmHomeDmSigil"><DmSigil /></span>
            <span className="vtmHomeDmText">
              <span className="vtmHomeDmTitle">{vtmT('dmCard')}</span>
              <span className="vtmHomeDmDesc">{vtmT('homeDmDesc')}</span>
            </span>
            <span className="vtmHomeArrow" aria-hidden="true">→</span>
          </Link>

          <div className="vtmHomeSectionHeader">
            <h2 className="vtmHomeSectionTitle">{vtmT('coterie')} · {characters.length}</h2>
            <button type="button" className="vtmHomeTextButton" onClick={onCreateCharacter}>+ {vtmT('newCharacter')}</button>
          </div>
          {characters.length === 0 ? (
            <p className="vtmHomeEmptyText">{vtmT('homeNoPlayer')}</p>
          ) : (
            <div className="vtmHomeGrid">
              {sorted.map(character => (
                <CharacterRow key={character.uid} character={character} baseUrl={baseUrl} onSelect={onSelectCharacter} showHunger />
              ))}
            </div>
          )}
        </>
      ) : mainCharacter ? (
        <>
          <Link className="vtmHomeHero" to={`${baseUrl}/${mainCharacter.uid}`} onClick={() => onSelectCharacter(mainCharacter)}>
            <span className="vtmHomeHeroPortrait"><Picture character={mainCharacter} frame="" /></span>
            <span className="vtmHomeHeroBody">
              <span className="vtmHomeKicker">{vtmT('homeYourCharacter')}</span>
              <span className="vtmHomeHeroName">{mainCharacter.name || vtmT('newCharacter')}</span>
              <Identity character={mainCharacter} />
              {!isDraft(mainCharacter) && mainCharacter.vtm && <Gauges sheet={mainCharacter.vtm} />}
              <span className="vtmHomeCta">{isDraft(mainCharacter) ? vtmT('homeResumeCreation') : vtmT('openSheet')} →</span>
            </span>
          </Link>

          {otherCharacters.length > 0 && (
            <>
              <h2 className="vtmHomeSectionTitle spaced">{vtmT('homeOtherCharacters')}</h2>
              <div className="vtmHomeGrid">
                {otherCharacters.map(character => (
                  <CharacterRow key={character.uid} character={character} baseUrl={baseUrl} onSelect={onSelectCharacter} />
                ))}
              </div>
            </>
          )}

          <button type="button" className="vtmHomeTextButton centered" onClick={onCreateCharacter}>
            + {vtmT('homeAnotherCharacter')}
          </button>
        </>
      ) : (
        <div className="vtmHomeEmpty">
          <h2>{vtmT('homeNoCharacterTitle')}</h2>
          <p>{vtmT('homeNoCharacterText')}</p>
          <button type="button" className="vtmButton primary" onClick={onCreateCharacter}>{vtmT('homeCreateCharacter')}</button>
        </div>
      )}
    </div>
  );
};

export default VtmCampaignHome;
